function getActivityId(activity) {
  return String(activity?._id || activity?.actionId || '');
}

function pinShapeId(pin) {
  return String(pin?.shapeId || '').trim();
}

function evidenceShapeId(evidence) {
  return String(evidence?.shapeId || '').trim();
}

export function placementPinCase(placement) {
  return String(placement?.pinCase || '').trim();
}

export function placementAlreadyJudged(placement) {
  return Boolean(placementPinCase(placement));
}

/**
 * Pins added since the last known canvas baseline.
 * No baseline (null/undefined) → none. Do not treat every pin on the board
 * as new — that re-runs pin-case embed + fill-judge for the whole canvas.
 * An explicit empty array means the board had zero pins, so every current
 * id is new.
 */
export function addedPinIdsSinceBaseline(previousPinIds, currentPinIds = []) {
  if (!Array.isArray(previousPinIds)) return [];
  const prevSet = new Set(
    previousPinIds.map(id => String(id || '')).filter(Boolean)
  );
  return (currentPinIds || [])
    .map(id => String(id || ''))
    .filter(id => id && !prevSet.has(id));
}

export function unjudgedInboxActivityIds(placements = []) {
  return (placements || [])
    .filter((placement) => {
      const source = String(placement?.placementSource || 'inbox');
      const isInbox = source === 'inbox' || source === '';
      return isInbox && !placementAlreadyJudged(placement);
    })
    .map(placement => String(placement.activityId || ''))
    .filter(Boolean);
}

/**
 * Visit pin-case: only pins added since the last known baseline.
 * Leftover inbox is not a new-pin signal — existing pins often stay inbox
 * until someone drags, so unioning every unjudged inbox id re-runs place +
 * pin-case (and the "Deciding where it belongs…" overlay) on every open.
 */
export function visitPinCaseRequest({
  previousPinIds = null,
  currentPinIds = [],
  placements = [],
} = {}) {
  const addedPinIds = addedPinIdsSinceBaseline(previousPinIds, currentPinIds);
  const inboxSet = new Set(unjudgedInboxActivityIds(placements));
  return {
    addedPinIds,
    newInboxIds: addedPinIds.filter(id => inboxSet.has(id)),
  };
}

function fillIdsFromProgress(userProgress) {
  const needles = new Set();
  const rows = [
    ...(Array.isArray(userProgress?.doneFills) ? userProgress.doneFills : []),
    ...(Array.isArray(userProgress?.partialFills) ? userProgress.partialFills : []),
  ];
  for (const entry of rows) {
    const activityId = String(entry?.activityId || '').trim();
    const shapeId = String(entry?.shapeId || '').trim();
    if (activityId) needles.add(activityId);
    if (shapeId) needles.add(shapeId);
  }
  return needles;
}

function judgedPlacementIds(placements = []) {
  const ids = new Set();
  for (const placement of placements || []) {
    if (!placementAlreadyJudged(placement)) continue;
    const activityId = String(placement?.activityId || '').trim();
    if (activityId) ids.add(activityId);
  }
  return ids;
}

function shapeAlreadyPinCased(shape) {
  const meta = shape?.meta && typeof shape.meta === 'object' ? shape.meta : {};
  return Boolean(
    String(meta.doneKey || '').trim()
    || String(meta.partialKey || '').trim()
    || String(meta.pinCase || '').trim()
  );
}

/**
 * Drop sources that already have a done/partial fill or a stored pinCase
 * so visit / add does not re-embed judged pins (including unrelated).
 */
export function dropAlreadyJudgedPinCaseSources({
  activityIds = [],
  aiPins = [],
  canvasEvidences = [],
  editor = null,
  userProgress = null,
  placements = null,
} = {}) {
  const judged = fillIdsFromProgress(userProgress);
  for (const id of judgedPlacementIds(placements)) judged.add(id);
  const nextActivityIds = (activityIds || [])
    .map(String)
    .filter(Boolean)
    .filter(id => !judged.has(id));
  const nextAiPins = (aiPins || []).filter((pin) => {
    if (!pin) return false;
    const shapeId = pinShapeId(pin) || String(pin.id || '').trim();
    if (shapeId && judged.has(shapeId)) return false;
    if (pin.activityId && judged.has(String(pin.activityId))) return false;
    if (shapeId && shapeAlreadyPinCased(editor?.getShape?.(shapeId))) return false;
    return true;
  });
  const nextEvidences = (canvasEvidences || []).filter((evidence) => {
    const shapeId = evidenceShapeId(evidence);
    if (!shapeId) return false;
    if (judged.has(shapeId)) return false;
    if (shapeAlreadyPinCased(editor?.getShape?.(shapeId))) return false;
    return true;
  });
  return {
    activityIds: nextActivityIds,
    aiPins: nextAiPins,
    canvasEvidences: nextEvidences,
  };
}

/**
 * Drop pin-case sources whose activity/shape was already removed.
 * AI pins without a tldraw shapeId cannot be verified and are kept.
 */
export function filterLivePinCaseSources({
  activityIds = [],
  aiPins = [],
  canvasEvidences = [],
  editor = null,
  activities = [],
} = {}) {
  const liveActivityIds = new Set(
    (activities || []).map(activity => getActivityId(activity)).filter(Boolean)
  );
  const nextActivityIds = (activityIds || [])
    .map(String)
    .filter(Boolean)
    .filter(id => liveActivityIds.has(id));
  const nextAiPins = (aiPins || []).filter((pin) => {
    if (!pin) return false;
    const shapeId = pinShapeId(pin);
    if (!shapeId) return true;
    return Boolean(editor?.getShape?.(shapeId));
  });
  const nextEvidences = (canvasEvidences || []).filter((evidence) => {
    const shapeId = evidenceShapeId(evidence);
    if (!shapeId) return false;
    return Boolean(editor?.getShape?.(shapeId));
  });
  return {
    activityIds: nextActivityIds,
    aiPins: nextAiPins,
    canvasEvidences: nextEvidences,
  };
}

export function gonePinCaseSources(requested = {}, live = {}) {
  const liveActivityIds = new Set((live.activityIds || []).map(String));
  const liveShapeIds = new Set();
  for (const pin of live.aiPins || []) {
    const shapeId = pinShapeId(pin);
    if (shapeId) liveShapeIds.add(shapeId);
  }
  for (const evidence of live.canvasEvidences || []) {
    const shapeId = evidenceShapeId(evidence);
    if (shapeId) liveShapeIds.add(shapeId);
  }

  const goneActivityIds = (requested.activityIds || [])
    .map(String)
    .filter(Boolean)
    .filter(id => !liveActivityIds.has(id));
  const goneShapeIds = [];
  for (const pin of requested.aiPins || []) {
    const shapeId = pinShapeId(pin);
    if (shapeId && !liveShapeIds.has(shapeId)) goneShapeIds.push(shapeId);
  }
  for (const evidence of requested.canvasEvidences || []) {
    const shapeId = evidenceShapeId(evidence);
    if (shapeId && !liveShapeIds.has(shapeId)) goneShapeIds.push(shapeId);
  }
  return {
    activityIds: [...new Set(goneActivityIds)],
    shapeIds: [...new Set(goneShapeIds)],
  };
}

export function hasLivePinCaseSources(sources = {}) {
  return Boolean(
    (sources.activityIds || []).length
    || (sources.aiPins || []).length
    || (sources.canvasEvidences || []).length
  );
}
